import React, { useEffect, useState } from 'react';
import { useTranslations } from 'i18n/use-translations';
import { Text } from '../text';

export interface SelectSearchStatusProps {
    /** The live search term; nothing is announced while it is empty. */
    searchValue: string;
    resultCount: number;
    /** Shown (and so announced) when nothing is listed; without it the empty result is announced only. */
    visibleEmptyMessage?: string;
}

/** Long enough for a burst of typing to settle, so only the final count is spoken. */
export const SEARCH_STATUS_DELAY_MS = 500;

/** Polite search result count. Mount it with the search field so only its text changes. */
export const SelectSearchStatus: React.FC<SelectSearchStatusProps> = ({
    searchValue,
    resultCount,
    visibleEmptyMessage,
}) => {
    const { __ } = useTranslations();

    let announcement = '';
    if (searchValue) {
        if (resultCount === 0) {
            announcement = __('misc.no_results_found');
        } else if (resultCount === 1) {
            announcement = __('misc.search_result_count_one');
        } else {
            announcement = __('misc.search_result_count', { num: String(resultCount) });
        }
    }

    // Mounts with the current count; each keystroke restarts the wait for a pause, and clearing is immediate.
    const [spoken, setSpoken] = useState(announcement);
    useEffect(() => {
        if (!announcement) {
            setSpoken('');
            return undefined;
        }
        const timer = setTimeout(() => setSpoken(announcement), SEARCH_STATUS_DELAY_MS);
        return () => clearTimeout(timer);
    }, [announcement, searchValue]);

    if (resultCount === 0 && visibleEmptyMessage) {
        return (
            <div role="status" className="px-3 py-2">
                <Text variant="label-large" className="text-system-on-surface-variant">
                    {visibleEmptyMessage}
                </Text>
            </div>
        );
    }

    return (
        <div role="status" className="sr-only">
            {spoken}
        </div>
    );
};
